import React, { useLayoutEffect, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import useTooltipShowOnBottom from '@/app/components/search/hooks/useTooltipShowOnBottom.ts';
import useTooltipState from '@/app/components/search/hooks/useTooltipState.ts';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { IconOrLegacyAsset } from '@/app/common/icons/IconOrLegacyAsset';
import { IconWithTooltip, TooltipContent, TooltipAnchor, TooltipIconAnchor } from '../styled.ts';
import { TooltipType } from '../TooltipType.ts';

type MobileFixed = { top?: number; bottom?: number; left: number; arrowLeft: number };

const calcMobileFixed = (
  anchor: Element,
  size: number,
  position: 'center' | 'left' | 'right',
  showOnBottom: boolean
): MobileFixed => {
  const rect = anchor.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const tooltipWidth = Math.min(size, window.innerWidth - 32);
  const minEdge = 32;
  const maxLeft = window.innerWidth - tooltipWidth - minEdge;
  const rawLeft = position === 'left' ? minEdge : position === 'right' ? maxLeft : centerX - tooltipWidth / 2;
  const left = Math.max(minEdge, Math.min(maxLeft, rawLeft));
  const arrowLeft = Math.max(10, Math.min(tooltipWidth - 10, centerX - left));
  return showOnBottom
    ? { top: rect.bottom + 8, left, arrowLeft }
    : { bottom: window.innerHeight - rect.top + 8, left, arrowLeft };
};

type TsxTooltipProps = {
  tooltip: React.ReactNode;
  iconSrc?: StarfishIconRef | string;
  text?: string;
  children?: React.ReactNode;
  lastWordStyle?: React.CSSProperties;
  size?: number;
  position?: 'center' | 'left' | 'right';
  isMobile?: boolean;
  isOpen?: boolean;
  tooltipRef?: React.RefObject<HTMLDivElement | null>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const TsxTooltip = ({
  tooltip,
  iconSrc,
  text,
  children,
  lastWordStyle,
  size = 200,
  position = 'center',
  isMobile,
  isOpen: isOpenProp,
  tooltipRef: externalRef,
  onMouseEnter,
  onMouseLeave,
}: TsxTooltipProps) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const imgRef = useRef<HTMLElement>(null);
  const tooltipRef = externalRef ?? internalRef;

  const controlled = isOpenProp !== undefined;
  const { isOpen: hookOpen, close, scheduleOpen, scheduleClose, toggle } = useTooltipState({ isMobile });
  const isOpen = controlled ? isOpenProp : hookOpen;

  const { showOnBottom } = useTooltipShowOnBottom(tooltipRef, isOpen, isMobile);
  const [mobileFixed, setMobileFixed] = useState<MobileFixed | null>(null);

  useOnclickOutside(
    () => {
      if (!controlled && isMobile && hookOpen) close();
    },
    // @ts-ignore
    { refs: [containerRef] }
  );

  useLayoutEffect(() => {
    const anchor = imgRef.current ?? containerRef.current;
    if (!isMobile || !isOpen || !anchor) {
      setMobileFixed(null);
      return;
    }
    setMobileFixed(calcMobileFixed(anchor, size, position, showOnBottom));
  }, [isOpen, isMobile, showOnBottom, size, position]);

  const handleMouseEnter = () => {
    if (!controlled) scheduleOpen();
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    if (!controlled) scheduleClose();
    onMouseLeave?.();
  };

  const handleClick = () => {
    if (isMobile && !controlled) toggle();
  };

  const tooltipContent =
    isOpen && (!isMobile || mobileFixed) ? (
      <TooltipContent
        ref={tooltipRef}
        tooltipSize={size}
        showOnBottom={showOnBottom}
        mobileFixed={mobileFixed}
        style={!mobileFixed ? { left: '50%', transform: 'translateX(-50%)' } : undefined}
      >
        {tooltip}
      </TooltipContent>
    ) : null;

  if (!iconSrc) {
    return (
      <TooltipAnchor
        ref={containerRef}
        role="button"
        tabIndex={controlled ? -1 : 0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
      >
        {children ?? <span dangerouslySetInnerHTML={{ __html: text! }} />}
        {tooltipContent}
      </TooltipAnchor>
    );
  }

  const icon = (
    <IconWithTooltip
      tooltipSize={size}
      tooltipType={TooltipType.primary}
      position={position}
      isMobile={isMobile}
      role={!controlled ? 'button' : undefined}
      aria-label={!controlled && typeof tooltip === 'string' ? tooltip : undefined}
      tabIndex={controlled ? -1 : 0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
    >
      <TooltipIconAnchor ref={imgRef}>
        <IconOrLegacyAsset icon={iconSrc} className="icon-right" size={16} />
      </TooltipIconAnchor>
      {tooltipContent}
    </IconWithTooltip>
  );

  if (!text && !children) return icon;

  return (
    <span ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children ? (
        <>
          {children}
          {icon}
        </>
      ) : (
        <AddElementToLastWordInString text={text!} lastWordStyle={lastWordStyle}>
          {icon}
        </AddElementToLastWordInString>
      )}
    </span>
  );
};

export default TsxTooltip;
